/**
 * HTTP client for POST /api/v2/fc/low (quality_mode=low / full-conversation-low).
 * Auth: AuthInterceptor adds Bearer + x-api-key. Never hits AgentCore/presign.
 */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpDownloadProgressEvent,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, OperatorFunction, defer } from 'rxjs';
import { tap, timeout } from 'rxjs/operators';
import { ConfigService } from 'src/app/services/config.service';
import { retryWithBackoff } from 'src/app/shared/utils/http-retry.util';

export type FcLowAction = 'boot' | 'start_questions' | 'submit_turn';

export type FcLowPhase =
  | 'greeting'
  /** Módulo de experiencia previa (3 turnos, sin `question`, no se califica). */
  | 'intro'
  | 'question'
  | 'followup'
  | 'farewell'
  | 'complete';

/** Progreso del módulo de experiencia previa (solo con phase 'intro'). */
export interface FcLowIntroProgress {
  turn: number;
  max: number;
}

export interface FcLowQuestion {
  id: string;
  text: string;
  knowledge: string;
  /**
   * Id del conocimiento del area. Es la unidad del nombre de los artefactos en
   * full_conversation (el video se corta por area, no por pregunta). Opcional
   * porque una sesion servida por un backend anterior no lo trae; en ese caso
   * el backend de presign cae al nombre historico.
   */
  knowledge_id?: string;
  index: number;
}

export interface FcLowBudgets {
  think_s: number;
  answer_s: number;
}

export type FcLowDetectedCommandType = 'pass' | 'repeat' | 'clarify' | 'end' | 'context';

/**
 * Redundant, FE-detected command signal — mirrors DetectedCommandHint on the
 * fc-low-api backend (src/core/common/types.ts). Additive: sent alongside
 * the existing sentinel/phrase transcript, never as a replacement for it —
 * low mode has no Gemini to lean on, so the BE regex + this FE regex hint
 * are two independent detection paths for the same commands.
 */
export interface FcLowDetectedCommandHint {
  command: FcLowDetectedCommandType;
  source: 'client_regex';
  confidence: number;
}

/**
 * Why the FE closed the turn and called submit_turn — plan §9.1 (rubric
 * contract, ola 3). The runtime distinguishes exactly these four triggers:
 * - eos_grace: silence after speech settled (the end-of-speech grace timer,
 *   short for a "complete"-looking final or the longer floor for a hung one).
 * - answer_budget: the hard per-question answer time budget expired.
 * - user_command: an explicit client-detected voice/UI command (pass,
 *   repeat, clarify, end) forced the turn closed.
 * - absence_timeout: the candidate never spoke — the absence ladder gave up.
 * - conferencing_tool: VC detection (CONTRATO-VC-DETECTION.md §0/§2/§4.2) —
 *   the client-side conferencingLadder decided to suspend (score/level from
 *   VcScorerService). Informational on the BE (same as the others — never
 *   gates a decision by itself), gated instead on the CONFERENCING_TRANSCRIPT
 *   sentinel, mirroring how absence_timeout pairs with ABSENT_TRANSCRIPT.
 */
export type FcLowEndReason =
  | 'eos_grace'
  | 'answer_budget'
  | 'user_command'
  | 'time_up'
  | 'absence_timeout'
  | 'conferencing_tool';

/**
 * Salvavidas (PLAN_LIFELINE.md §3.2) — snapshot del affect engine al cerrar el
 * turno. Señales RUIDOSAS: el BE les pone tope (0.25) y nunca disparan solas
 * el salvavidas. Todo opcional y retrocompatible: un BE viejo lo ignora y el
 * controller nuevo descarta cualquier campo inválido (nunca 400 por esto).
 */
export interface FcLowAffectSignals {
  /** Eje strain del engine, [0,1]. */
  strain?: number;
  /** Densidad de muletillas del transcript, [0,1]. */
  hesitation_density?: number;
  /** Proxy de auto-corrección del transcript, [0,1]. */
  self_repair_rate?: number;
  /** ms desde la apertura de la ventana hasta la primera habla; -1 = desconocido. */
  time_to_first_speech_ms?: number;
  /** Léxico explícito de apuro/confusión ("nervioso", "perdido"…). */
  has_distress?: boolean;
  /** Memoria de sesión: ms de habla efectiva del turno (pausas largas descontadas). */
  speech_duration_ms?: number;
  /** Palabras/min del turno, <=600; se omite con habla < 1s. */
  words_per_minute?: number;
  /** Pausas internas >= 2s dentro del turno del candidato. */
  long_pause_count?: number;
  /** Veces que el candidato habló mientras Eva narraba (ciclo del turno). Solo se manda si > 0. */
  barge_in_count?: number;
}

/** Disparo del salvavidas (P2–P6; P1 "no sé" se quitó el 2026-10-02) — solo debug. */
export type FcLowLifelineTrigger =
  | 'weak_refusal'
  | 'clarify_cap'
  | 'no_progress'
  | 'explicit_request'
  // v3 (P6): "estoy nervioso / me quedé en blanco" — pedido de ayuda implícito.
  | 'explicit_distress';

/**
 * Estado del salvavidas que el BE devuelve en TODAS las respuestas (con el
 * feature encendido). `served_this_turn` = esta respuesta ES la pista
 * (reason `lifeline_hint`). `criterion_key` null = guía genérica.
 */
export interface FcLowLifeline {
  used: number;
  max: number;
  served_this_turn: boolean;
  criterion_key: string | null;
}

export interface FcLowRequest {
  testId: string;
  email: string;
  sessionId: string;
  action: FcLowAction;
  user_transcript?: string;
  /** Pace profile from affect engine → turn_silence_s ladder. */
  adaptation_profile?: string;
  /** Optional redundant command signal — see FcLowDetectedCommandHint. */
  detected_command_hint?: FcLowDetectedCommandHint;
  /**
   * Plan §9.1 — result of the FE's `looksIncompleteTranscript` (a final reads as
   * "cut off" if it ends in a dangling word/connector/filler or has < 3 words;
   * missing terminal punctuation alone no longer counts — the browser ASR
   * never punctuates).
   * AUXILIARY signal only. The BE's own `looksDanglingTranscript` (STRICT —
   * dangling-word list) is what decides whether to retry the turn; this
   * field must never become the sole retry trigger on either side. Additive
   * and optional — a BE that does not read it keeps working unchanged.
   */
  transcript_incomplete?: boolean;
  /** Why the FE closed the turn — see FcLowEndReason. Additive/optional. */
  end_reason?: FcLowEndReason;
  /** Salvavidas — snapshot del affect engine (ver FcLowAffectSignals). */
  affect_signals?: FcLowAffectSignals;
  /**
   * Idempotencia (submit_turn): `question.index` y `turn_seq` de la ÚLTIMA
   * respuesta que el FE aplicó. Si no coinciden con el estado del BE (POST
   * duplicado / replay / carrera) este responde el estado actual con
   * reason `stale_turn` sin avanzar. Opcionales: un BE viejo los ignora.
   */
  expected_question_idx?: number;
  expected_turn_seq?: number;
  /**
   * Greeting submit: el FE ya no habla acuse local (el BE dice "Mucho gusto, {name}."
   * en su narración), por eso hoy viaja false. Legado de BE viejo (evitar repetir saludo).
   */
  greeting_ack_spoken?: boolean;
  /**
   * El FE dijo un conector (acuse) en ESTE turno. Generaliza `greeting_ack_spoken`.
   * Ausente ⇒ el BE asume true. El BE lo usa para no apilar un segundo acuse.
   */
  ack_spoken?: boolean;
  ack_mode?: 'server';
  /**
   * turn_seq de una respuesta de Eva que el candidato NUNCA escuchó (parkeada y
   * descartada porque habló encima). El BE hace rollback de ese prompt y decide.
   */
  unheard_turn_seq?: number;
  /**
   * Enunciado abierto del candidato. El mismo id viaja en cada revisión hasta
   * que el candidato oye a Eva. Ausente en comandos, sentinelas y FE viejo.
   */
  utterance_id?: string;
  /** 1 en el primer POST; +1 cuando el texto crece de verdad. */
  utterance_revision?: number;
  /** Segundos que quedaban del timer de respuesta del shell y su total (priorización por dimensión). */
  answer_remaining_s?: number;
  answer_budget_s?: number;
}

/** Opciones nombradas extra de submit_turn (contrato BE polish). */
export interface FcLowSubmitExtras {
  greetingAckSpoken?: boolean;
  /** El FE habló un conector en este submit. */
  ackSpoken?: boolean;
  ackMode?: 'server';
  unheardTurnSeq?: number;
  /** Id estable del enunciado. No se manda en comandos ni sentinelas. */
  utteranceId?: string;
  utteranceRevision?: number;
  /** Timer de respuesta del shell al cerrar el turno (priorización por dimensión en el BE). */
  answerTimer?: FcLowAnswerTimer | null;
}

/** Segundos que quedaban del timer de respuesta y su total (solo lectura del shell). */
export interface FcLowAnswerTimer {
  remainingS: number;
  budgetS: number;
}

/** Qué hizo el BE con el prompt del turno (followup nuevo vs reescritura vs repetición). */
export type FcLowPromptAction = 'new_followup' | 'rewrite_question' | 'rewrite_followup' | 'repose';

/** Punteros de idempotencia que el runtime manda en submit_turn. */
export interface FcLowExpectedState {
  questionIdx?: number;
  turnSeq?: number;
}

export interface FcLowResponse {
  quality_mode: 'low';
  narration_text: string;
  display_text: string;
  phase: FcLowPhase;
  question: FcLowQuestion | null;
  mic_open: boolean;
  interview_complete: boolean;
  reason: string | null;
  budgets: FcLowBudgets | null;
  turn_seq: number;
  /** Total questions in bank — progress UI. */
  total_questions?: number;
  /** Post-answer silence window (seconds) before auto-pass. */
  turn_silence_s?: number;
  /** Segundos ya transcurridos — solo presente al retomar una sesion existente. */
  elapsed_seconds?: number;
  /** Conclusión de la pregunta que se deja — se narra ANTES del overlay Q→Q. */
  advance_ack?: string;
  /** DEBUG (panel dev de criterios) — el runtime lo traduce a criteria_eval. */
  criteria_eval?: FcLowCriteriaEval | null;
  /** Salvavidas — cupo usado/máximo y si esta respuesta sirvió la pista. */
  lifeline?: FcLowLifeline;
  /** Qué hizo el BE con el prompt (ausente en BE viejo → fallback por `reason`). */
  prompt_action?: FcLowPromptAction;
  /**
   * Nombre de pila validado por el BE (null = sin nombre). Única fuente del nombre
   * en el FE (check-in de ausencia). Ausente en BE viejo.
   */
  candidate_name?: string | null;
  /**
   * Tipo del prompt activo tras este turno. 'confirm_advance' = Eva preguntó si quiere
   * agregar algo más: el shell congela el timer de la pregunta mientras decide.
   * 'pause' = Eva concedió una pausa de `pause_s` segundos (timer congelado).
   */
  active_prompt_kind?: 'question' | 'followup' | 'confirm_advance' | 'pause';
  /** Solo con active_prompt_kind 'pause': duración de la pausa (s). */
  pause_s?: number;
  /** Solo con active_prompt_kind 'pause': segundos de prueba que quedan (sin pausas). */
  session_remaining_s?: number;
  ack_stream?: boolean;
  /** Módulo de experiencia previa — presente con phase 'intro'. */
  intro?: FcLowIntroProgress;
}

export interface FcLowCriteriaEval {
  decision_kind: string;
  decision_reason: string;
  missing_aspect: string;
  criteria: {
    key: string;
    description: string;
    required: boolean;
    covered: boolean;
  }[];
  /** DEBUG salvavidas — índice de apuro [0,1] calculado en este turno. */
  distress_score?: number;
  /** DEBUG salvavidas — momento de pérdida que lo evaluó (null = ninguno). */
  lifeline_trigger?: FcLowLifelineTrigger | null;
}

/** Content-Type con el que el BE marca la respuesta "streaming por etapas". */
export const FC_LOW_NDJSON_CONTENT_TYPE = 'application/x-ndjson';

/** Tope de la fase pre-decision (mismo valor que el timeout historico del POST). */
export const FC_LOW_PRE_DECISION_TIMEOUT_MS = 30_000;

/**
 * Mensajes NDJSON del BE:
 * - decision: FcLowResponse tal cual el JSON buffered de siempre.
 * - done: la persistencia termino.
 * - error: la persistencia FALLO despues de enviar la decision.
 */
export type FcLowStreamMessage =
  | { type: 'ack'; category: string }
  | { type: 'decision'; data: FcLowResponse }
  | { type: 'done' }
  | { type: 'error'; error: string; message?: string };

export interface FcLowAck {
  category: string;
}

/** Evento de dominio que emite la llamada de turno (ya sin detalles de HTTP). */
export type FcLowStreamEvent =
  | { kind: 'decision'; response: FcLowResponse }
  | { kind: 'done' };

/** Error propio del protocolo de stream (parseo / truncado / evento error). Nunca se reintenta. */
export class FcLowStreamError extends Error {
  constructor(
    message: string,
    readonly code: string = 'STREAM_PROTOCOL_ERROR',
  ) {
    super(message);
    this.name = 'FcLowStreamError';
  }
}

/** Persistencia diferida fallida (evento `error` del stream, post-decision). */
export class FcLowDeferredWorkError extends FcLowStreamError {
  constructor(message: string) {
    super(message, 'DEFERRED_WORK_FAILED');
    this.name = 'FcLowDeferredWorkError';
  }
}

/**
 * Parser NDJSON incremental. Tolera chunks partidos a mitad de linea, varias
 * lineas por chunk, lineas en blanco y \r\n. Una linea que no es JSON (o no
 * tiene la forma esperada) lanza FcLowStreamError. end() procesa una ultima
 * linea sin salto de linea final.
 */
export class FcLowNdjsonParser {
  private buffer = '';

  push(chunk: string): FcLowStreamMessage[] {
    this.buffer += chunk;
    const out: FcLowStreamMessage[] = [];
    let idx: number;
    while ((idx = this.buffer.indexOf('\n')) >= 0) {
      const line = this.buffer.slice(0, idx);
      this.buffer = this.buffer.slice(idx + 1);
      const msg = FcLowNdjsonParser.parseLine(line);
      if (msg) out.push(msg);
    }
    return out;
  }

  end(): FcLowStreamMessage[] {
    const rest = this.buffer;
    this.buffer = '';
    const msg = FcLowNdjsonParser.parseLine(rest);
    return msg ? [msg] : [];
  }

  private static parseLine(raw: string): FcLowStreamMessage | null {
    const line = raw.trim();
    if (!line) return null;
    let parsed: any;
    try {
      parsed = JSON.parse(line);
    } catch {
      throw new FcLowStreamError(`Linea NDJSON invalida: ${line.slice(0, 80)}`);
    }
    if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') {
      throw new FcLowStreamError(`Linea NDJSON sin "type": ${line.slice(0, 80)}`);
    }
    if (parsed.type === 'ack') {
      const category = parsed.data?.category;
      return typeof category === 'string' && category ? { type: 'ack', category } : null;
    }
    if (parsed.type === 'decision') {
      if (!parsed.data || typeof parsed.data !== 'object') {
        throw new FcLowStreamError('Evento decision sin "data"');
      }
      return { type: 'decision', data: parsed.data as FcLowResponse };
    }
    if (parsed.type === 'done') return { type: 'done' };
    if (parsed.type === 'error') {
      return {
        type: 'error',
        error: typeof parsed.error === 'string' ? parsed.error : 'DEFERRED_WORK_FAILED',
        message: typeof parsed.message === 'string' ? parsed.message : undefined,
      };
    }
    // Tipo desconocido: se ignora (compat hacia adelante).
    return null;
  }
}

/** Resultado de submitTurnStream: ver FcLowApiService.submitTurnStream. */
export interface FcLowTurnStream {
  /** Resuelve con la decision (evento `decision` o JSON buffered). */
  decision: Promise<FcLowResponse>;
  /**
   * Resuelve en `done` (o apenas llega la decision en modo buffered); rechaza
   * en el evento `error`, stream truncado o error de red post-decision.
   */
  settled: Promise<void>;
  ack?: Promise<FcLowAck | null>;
  /** Corta la suscripcion HTTP (aborta el XHR). Idempotente. */
  cancel?: () => void;
}

/** Body de submit_turn — compartido por submitTurn() y por el runtime. */
export function buildSubmitTurnRequest(
  testId: string,
  email: string,
  sessionId: string,
  transcript: string,
  adaptationProfile?: string | null,
  detectedCommandHint?: FcLowDetectedCommandHint | null,
  transcriptIncomplete?: boolean,
  endReason?: FcLowEndReason,
  affectSignals?: FcLowAffectSignals | null,
  expected?: FcLowExpectedState | null,
  extras?: FcLowSubmitExtras | null,
): FcLowRequest {
  return {
    testId,
    email,
    sessionId,
    action: 'submit_turn',
    user_transcript: transcript,
    ...(adaptationProfile ? { adaptation_profile: adaptationProfile } : {}),
    ...(detectedCommandHint ? { detected_command_hint: detectedCommandHint } : {}),
    ...(transcriptIncomplete !== undefined ? { transcript_incomplete: transcriptIncomplete } : {}),
    ...(endReason ? { end_reason: endReason } : {}),
    ...(affectSignals ? { affect_signals: affectSignals } : {}),
    ...(typeof expected?.questionIdx === 'number'
      ? { expected_question_idx: expected.questionIdx }
      : {}),
    ...(typeof expected?.turnSeq === 'number' ? { expected_turn_seq: expected.turnSeq } : {}),
    ...(typeof extras?.greetingAckSpoken === 'boolean'
      ? { greeting_ack_spoken: extras.greetingAckSpoken }
      : {}),
    ...(typeof extras?.ackSpoken === 'boolean' ? { ack_spoken: extras.ackSpoken } : {}),
    ...(extras?.ackMode === 'server' ? { ack_mode: 'server' as const } : {}),
    ...(typeof extras?.unheardTurnSeq === 'number'
      ? { unheard_turn_seq: extras.unheardTurnSeq }
      : {}),
    ...(extras?.utteranceId
      ? {
          utterance_id: extras.utteranceId,
          ...(typeof extras.utteranceRevision === 'number'
            ? { utterance_revision: extras.utteranceRevision }
            : {}),
        }
      : {}),
    ...answerTimerFields(extras?.answerTimer),
  };
}

function answerTimerFields(timer: FcLowAnswerTimer | null | undefined): {
  answer_remaining_s?: number;
  answer_budget_s?: number;
} {
  if (!timer) return {};
  return { answer_remaining_s: timer.remainingS, answer_budget_s: timer.budgetS };
}

@Injectable({ providedIn: 'root' })
export class FcLowApiService {
  constructor(
    private http: HttpClient,
    private config: ConfigService,
  ) {}

  boot(
    testId: string,
    email: string,
    sessionId: string,
    adaptationProfile?: string | null,
  ): Observable<FcLowResponse> {
    return this.post({
      testId,
      email,
      sessionId,
      action: 'boot',
      ...(adaptationProfile ? { adaptation_profile: adaptationProfile } : {}),
    });
  }

  startQuestions(
    testId: string,
    email: string,
    sessionId: string,
    adaptationProfile?: string | null,
  ): Observable<FcLowResponse> {
    return this.post({
      testId,
      email,
      sessionId,
      action: 'start_questions',
      ...(adaptationProfile ? { adaptation_profile: adaptationProfile } : {}),
    });
  }

  submitTurn(
    testId: string,
    email: string,
    sessionId: string,
    transcript: string,
    adaptationProfile?: string | null,
    detectedCommandHint?: FcLowDetectedCommandHint | null,
    transcriptIncomplete?: boolean,
    endReason?: FcLowEndReason,
    affectSignals?: FcLowAffectSignals | null,
    expected?: FcLowExpectedState | null,
    extras?: FcLowSubmitExtras | null,
  ): Observable<FcLowResponse> {
    return this.post(
      buildSubmitTurnRequest(
        testId,
        email,
        sessionId,
        transcript,
        adaptationProfile,
        detectedCommandHint,
        transcriptIncomplete,
        endReason,
        affectSignals,
        expected,
        extras,
      ),
    );
  }

  /**
   * Turno con "streaming por etapas". Mismo POST (misma URL, x-api-key y TODOS
   * los HttpInterceptor: Auth, LocalOverride, ErrorHandler) pero con
   * `Accept: application/x-ndjson`, `reportProgress` y responseType text para
   * leer el body incrementalmente (HttpDownloadProgressEvent.partialText).
   * Se eligio HttpClient y no fetch a proposito: fetch directo se saltearia el
   * AuthInterceptor (Bearer + refresh), el override local y el manejo de 401/503.
   *
   * El modo se decide por Content-Type de la respuesta: x-ndjson = stream;
   * cualquier otro (application/json de un BE buffered/viejo) = JSON clasico,
   * decision y settled resuelven juntos.
   *
   * Reintento/timeout (http-retry.util) solo en la fase PRE-decision; una vez
   * entregada la decision NO se re-POSTea (duplicaria el turno).
   */
  submitTurnStream(req: FcLowRequest): FcLowTurnStream {
    let resolveDecision!: (r: FcLowResponse) => void;
    let rejectDecision!: (e: unknown) => void;
    let resolveSettled!: () => void;
    let rejectSettled!: (e: unknown) => void;
    const decision = new Promise<FcLowResponse>((res, rej) => {
      resolveDecision = res;
      rejectDecision = rej;
    });
    const settled = new Promise<void>((res, rej) => {
      resolveSettled = res;
      rejectSettled = rej;
    });
    // Un rechazo pre-decision lo consume `decision`; evita unhandledrejection en settled.
    settled.catch(() => undefined);
    let resolveAck!: (a: FcLowAck | null) => void;
    const ack = new Promise<FcLowAck | null>((res) => {
      resolveAck = res;
    });

    const subscription = this.streamTurn(req, (category) => resolveAck({ category })).subscribe({
      next: (ev) => {
        if (ev.kind === 'decision') {
          resolveAck(null);
          resolveDecision(ev.response);
        } else {
          resolveSettled();
        }
      },
      error: (err) => {
        resolveAck(null);
        rejectDecision(err);
        rejectSettled(err);
      },
      complete: () => {
        // Completar sin decision/done (p.ej. un interceptor devolvio EMPTY).
        const e = new FcLowStreamError('Respuesta vacia del servidor', 'EMPTY_RESPONSE');
        resolveAck(null);
        rejectDecision(e);
        rejectSettled(e);
      },
    });
    return { decision, settled, ack, cancel: () => subscription.unsubscribe() };
  }

  private streamTurn(
    body: FcLowRequest,
    onAck: (category: string) => void,
  ): Observable<FcLowStreamEvent> {
    const url = this.config.urlFcLow;
    const headers = new HttpHeaders()
      .set('x-api-key', this.config.apiKey)
      .set('Accept', FC_LOW_NDJSON_CONTENT_TYPE);

    return defer(() => {
      let decisionSeen = false;
      const attempt$ = defer(() =>
        this.http
          .post(url, body, {
            headers,
            observe: 'events',
            reportProgress: true,
            responseType: 'text',
          })
          .pipe(
            this.toStreamEvents(url, onAck),
            // Solo la fase pre-decision (primera emision) tiene tope de 30 s.
            timeout({ first: FC_LOW_PRE_DECISION_TIMEOUT_MS }),
            tap((ev) => {
              if (ev.kind === 'decision') decisionSeen = true;
            }),
          ),
      );
      // submit_turn NO es idempotente por sí solo. Reintento pre-decisión SOLO ante
      // errores de red/5xx (la petición pudo no llegar). El TimeoutError de 30 s NUNCA
      // se reintenta (retryWithBackoff lo corta): el BE pudo recibir y avanzar. Aun
      // así un reintento por red puede duplicar un POST ya guardado: ahí rige la
      // protección BE (expected_* → reason `stale_turn`) y el runtime lo re-sincroniza
      // (applyStaleTurn: narra la pregunta activa si el estado avanzó).
      return attempt$.pipe(
        retryWithBackoff({
          count: 2,
          baseDelayMs: 800,
          skip4xx: true,
          shouldRetry: (err) => !decisionSeen && !(err instanceof FcLowStreamError),
        }),
      );
    });
  }

  /** HttpEvent<string> → eventos de dominio, decidiendo ndjson vs json por Content-Type. */
  private toStreamEvents(
    url: string,
    onAck: (category: string) => void,
  ): OperatorFunction<HttpEvent<string>, FcLowStreamEvent> {
    return (source) =>
      new Observable<FcLowStreamEvent>((sub) => {
        let mode: 'unknown' | 'ndjson' | 'json' = 'unknown';
        const parser = new FcLowNdjsonParser();
        let offset = 0;
        let sawDecision = false;
        let sawDone = false;
        const detect = (headers: HttpHeaders | null | undefined) => {
          const ct = (headers?.get('Content-Type') ?? '').toLowerCase();
          mode = ct.includes(FC_LOW_NDJSON_CONTENT_TYPE) ? 'ndjson' : 'json';
        };
        const handle = (msgs: FcLowStreamMessage[]) => {
          for (const m of msgs) {
            if (m.type === 'ack') {
              if (!sawDecision) onAck(m.category);
            } else if (m.type === 'decision') {
              sawDecision = true;
              sub.next({ kind: 'decision', response: m.data });
            } else if (m.type === 'done') {
              sawDone = true;
              sub.next({ kind: 'done' });
            } else if (sawDecision) {
              throw new FcLowDeferredWorkError(m.message || m.error);
            } else {
              throw new FcLowStreamError(m.message || m.error, m.error);
            }
          }
        };
        const subscription = source.subscribe({
          next: (ev) => {
            try {
              if (ev.type === HttpEventType.ResponseHeader) {
                detect(ev.headers);
              } else if (ev.type === HttpEventType.DownloadProgress) {
                if (mode !== 'ndjson') return;
                const partial = (ev as HttpDownloadProgressEvent).partialText ?? '';
                handle(parser.push(partial.slice(offset)));
                offset = partial.length;
              } else if (ev.type === HttpEventType.Response) {
                if (mode === 'unknown') detect(ev.headers);
                const text = typeof ev.body === 'string' ? ev.body : '';
                if (mode === 'ndjson') {
                  handle(parser.push(text.slice(offset)));
                  handle(parser.end());
                  if (!sawDone) throw new FcLowStreamError('Stream terminado sin evento done');
                } else {
                  let json: FcLowResponse;
                  try {
                    json = JSON.parse(text) as FcLowResponse;
                  } catch {
                    // Mismo tratamiento que el ErrorHandlerInterceptor da a HTML-como-JSON.
                    throw new HttpErrorResponse({
                      status: 502,
                      statusText: 'Bad Gateway (non-JSON response)',
                      url,
                    });
                  }
                  sub.next({ kind: 'decision', response: json });
                  sub.next({ kind: 'done' });
                }
                sub.complete();
              }
            } catch (e) {
              sub.error(e);
            }
          },
          error: (e) => sub.error(e),
          complete: () => sub.complete(),
        });
        return () => subscription.unsubscribe();
      });
  }

  private post(body: FcLowRequest): Observable<FcLowResponse> {
    const url = this.config.urlFcLow;
    const headers = new HttpHeaders().set('x-api-key', this.config.apiKey);

    return this.http.post<FcLowResponse>(url, body, { headers }).pipe(
      timeout(30_000),
      retryWithBackoff({ count: 2, baseDelayMs: 800, skip4xx: true }),
    );
  }
}
